import { Clip } from '../types/clip-types';
import { ScreenshareBound, SpeakersAnalysis, VisionApiScreenshareResponse } from '../types/speakers-analysis';
import { getClipBroadcastId } from '../utils/clipUtils';
import { roundToNDecimalPlaces } from '../utils/mathUtils';
import { timeStringToSeconds } from '../utils/timeUtils';
import { findAdjustedBoxBoundaries } from '../utils/speakersAnalysisUtils';
import { EnvironmentVariable, getEnvValue } from '../env';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { buildAuthRequestHeaders } from '../utils/buildAuthRequestHeaders';
import { BaseLogger, NoOpLogger } from '../classes/Logger';
import { PlayerConfigLogEvent } from '../constants/logEvents';

export const defaultSpeakersAnalysis: SpeakersAnalysis = {
  face_ids: [],
  time_analysis: [],
  times: [],
  visible_face_ids: [],
  speaker_mapping: {},
  loading: true,
  adjustedBoxBoundaries: [],
  active_speaker_mapping: {}
};

export interface BaseSpeakerAnalysisOptions {
  clipId: string;
  clip?: Clip | null;
  onAsdComplete?: (clipId: string) => void;
  AuthHeader: string;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isScreenshareDetectionEnabled: boolean;
  logger?: BaseLogger;
}

interface PerClipSpeakersAnalysisOptions extends BaseSpeakerAnalysisOptions {
  start: number;
}

interface FullVideoSpeakersAnalysisOptions extends BaseSpeakerAnalysisOptions {
  contentId: string;
}

export type SpeakersAnalysisOptions = PerClipSpeakersAnalysisOptions | FullVideoSpeakersAnalysisOptions;

const reuseFullFacialRecDataForClip = ({ options }: { options: SpeakersAnalysisOptions }): boolean => {
  if ('contentId' in options) {
    const existingAnalysis =
      options.contentMetadataStoreInstance.getState().data[options.contentId]?.metadata.speakerAnalysis;
    if (existingAnalysis?.face_rec_backend === 'arcface') {
      return true;
    }
  }
  return false;
};

export const getSpeakersAnalysis = async (options: SpeakersAnalysisOptions): Promise<void> => {
  if (reuseFullFacialRecDataForClip({ options })) {
    return;
  }
  const { clipId, contentMetadataStoreInstance } = options;

  const url =
    'contentId' in options
      ? `${getEnvValue(EnvironmentVariable.VISION_URL)}/analysis?content_id=${options.contentId}`
      : `${getEnvValue(EnvironmentVariable.VISION_URL)}/analysis?clip_id=${clipId}`;

  const logger = options.logger ?? new NoOpLogger();

  logger.info(
    {
      event: PlayerConfigLogEvent.SPEAKERS_ANALYSIS_OUTBOUND,
      url,
      options
    },
    'Speaker analysis outbound request'
  );
  const speakerAnalysisTime = Date.now();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthRequestHeaders(options.AuthHeader)
    },
    mode: 'cors'
  });

  if (response.status !== 200) {
    logger.error({ event: PlayerConfigLogEvent.SPEAKERS_ANALYSIS_FAIL }, 'Speaker analysis fail');
    throw new Error('Error fetching speakers analysis');
  }

  const data: Pick<
    SpeakersAnalysis,
    | 'accurate'
    | 'time_analysis'
    | 'face_ids'
    | 'speaker_mapping'
    | 'active_speaker_mapping'
    | 'face_recognition_status'
    | 'face_rec_backend'
  > & {
    screenshares?: VisionApiScreenshareResponse[];
  } = await response.json();

  logger.info(
    { event: PlayerConfigLogEvent.SPEAKERS_ANALYSIS_RESPONSE, durationMs: Date.now() - speakerAnalysisTime, data },
    'Speaker analysis response'
  );

  const {
    accurate,
    face_ids = [],
    time_analysis = [],
    speaker_mapping = {},
    active_speaker_mapping = {},
    face_recognition_status,
    face_rec_backend,
    screenshares
  } = data;

  const updatedTimes = time_analysis.map(t => roundToNDecimalPlaces(timeStringToSeconds(t.time), 4));

  const adjustedBoxBoundaries = findAdjustedBoxBoundaries(time_analysis);

  const shouldProcessScreenshare = options.isScreenshareDetectionEnabled;
  let screenshare_bounds: ScreenshareBound[] | undefined;

  if (shouldProcessScreenshare && screenshares && screenshares.length > 0) {
    screenshare_bounds = screenshares.map(screenshare => ({
      start_time: roundToNDecimalPlaces(screenshare.start_time, 4),
      end_time: roundToNDecimalPlaces(screenshare.end_time, 4),
      coordinates: {
        x: screenshare.coordinates.x,
        y: screenshare.coordinates.y,
        width: screenshare.coordinates.width,
        height: screenshare.coordinates.height
      }
    }));
  }

  const isFaceRecDone = time_analysis.length > 0;

  const speakerAnalysisData: SpeakersAnalysis = {
    accurate,
    face_ids,
    time_analysis,
    times: updatedTimes,
    visible_face_ids: face_ids.slice(0, 4),
    speaker_mapping,
    loading: !isFaceRecDone,
    adjustedBoxBoundaries,
    active_speaker_mapping,
    screenshare_bounds,
    face_recognition_status,
    face_rec_backend
  };

  if ('contentId' in options) {
    contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
      contentId: options.contentId,
      speakerAnalysis: speakerAnalysisData
    });
  } else {
    const broadcastId = options.clip ? getClipBroadcastId(options.clip) : null;
    if (broadcastId) {
      contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
        contentId: broadcastId,
        speakerAnalysis: speakerAnalysisData
      });
    }
  }

  if (accurate === true && Object.keys(active_speaker_mapping).length > 0) {
    options.onAsdComplete?.(clipId);
  }
};
